import { Configuration, SuppliersApi } from '@/api';
import useGetOrganizationQuery from '@/api/queries/useGetOrganizationQuery';
import useGetSupplierQuery from '@/api/queries/useGetSupplierQuery';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import SupplierAddressForm from '@/modules/supplier/components/SupplierAddressForm';
import {
  findSupplierAddress,
  Supplier,
  SupplierAddress,
  SupplierAddressType,
} from '@/modules/supplier/models/Supplier';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Address = () => {
  const { currentUser } = useAuth();

  const { supplierId } = currentUser();

  const suppliersApi = new SuppliersApi(new Configuration(useAuth));

  const {
    isLoading: isLoadingSupplier,
    supplier,
    invalidateQuery,
  } = useGetSupplierQuery(supplierId);

  const { isLoading: isLoadingOrganization, organization } = useGetOrganizationQuery(
    supplier?.organizationId,
  );

  if (isLoadingSupplier || isLoadingOrganization) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!supplier || !organization) {
    return;
  }

  const handleSubmitSupplierAddress = async (supplier: Supplier, address: SupplierAddress) => {
    const messages = {
      [SupplierAddressType.BUSINESS]: 'Business address updated successfully!',
      [SupplierAddressType.BILLING]: 'Billing address updated successfully!',
      [SupplierAddressType.UNKNOWN]: 'Address updated successfully!',
    };

    try {
      const addresses = supplier.addresses ?? [];

      const existingIndex = addresses.findIndex((addr) => addr.id === address.id);

      const updatedAddresses =
        existingIndex >= 0
          ? addresses.map((addr, i) => (i === existingIndex ? { ...addr, ...address } : addr))
          : [...addresses, address];

      await suppliersApi.updateSupplier(supplier.id, {
        ...supplier,
        addresses: updatedAddresses,
      });

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={messages[address.type] || 'Address updated successfully!'}
          visible={t.visible}
        />
      ));

      await invalidateQuery();
    } catch (error) {
      console.error('Error while updating supplier address:', error);
    }
  };

  const businessAddress = findSupplierAddress(supplier.addresses, [SupplierAddressType.BUSINESS]);

  return (
    <>
      <SettingsHeader title="Business Address" icon={<BuildingOfficeIcon className="w-6 h-6" />} />
      <SupplierAddressForm
        address={businessAddress}
        onSubmit={(value) => handleSubmitSupplierAddress(supplier, value)}
        supplierId={supplier.id}
        supplierAddressType={SupplierAddressType.BUSINESS}
        countryCode={organization.countryCode}
      />
      `
    </>
  );
};

export default Address;
